/**
 * Board ページ — カンバン UI（旧 Flask SPA の機能パリティ・React 版）。
 *
 * 構成: カンバン列 / タスク詳細（スレッド・成果物・回答/差し戻し/承認）/
 * 登録フォーム（必須3＋任意5・受付開始スイッチ）/ エージェントタブ
 * （自由会話・記憶管理・顔アップロード・再インタビュー）/ チュートリアル /
 * ナレッジ管理。デザインは FAST のライトテーマ・デザイントークンに準拠。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useIsMobile } from "@/hooks/UseMobile"
import { AppHeader } from "@/components/AppHeader"
import { BottomTabBar } from "@/components/BottomTabBar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { formatSizeKB } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Artifact,
  BoardMe,
  BoardTask,
  ThreadEntry,
  Recurrence,
  answerTask,
  applyKnowledge,
  dismissKnowledge,
  approveTask,
  blockTask,
  unblockTask,
  createRecurrence,
  listRecurrences,
  pauseRecurrence,
  resumeRecurrence,
  deleteRecurrence,
  updateRecurrence,
  getArtifacts,
  getKnowledgeScopes,
  getMe,
  getQueueStatus,
  getThread,
  listTasks,
  QueueStatus,
  registerTask,
  sendbackTask,
  startIntake,
  uploadAttachment,
} from "@/services/boardService"
import { PreviewModal } from "@/components/common/PreviewModal"
import { useMyUsage } from "@/hooks/useMyUsage"
import { TenantMonthlyBanner } from "@/components/usage/TenantMonthlyBanner"

// 「オーナー問い合わせ中」は専用列を廃止（山本さん決定・利用ケースが想定されない）。
// ask_owner 自体は残し、その間のタスクは「実行中」列に置き、カードに ❓回答待ち バッジを出す。
// 列色は Polaris Night パレットに統一（2026-07 デスクトップ再構成）。
// 金=人の番待ち・灰=未着手・青緑=エージェント稼働・紺=完了・赤=ブロックのみ、の意味づけに揃える。
const COLS = [
  { label: "下書き／受付質疑", match: ["下書き", "受付質疑中"], color: "#B5760F" },
  // 「未着手」→「実行待ち」に改称（ブリーフ確定・キュー投入済みの実態に合わせる）。
  // 既存 DB に残る旧値「未着手」も同じ列に出すため両方マッチさせる（新旧混在で消えない）。
  { label: "実行待ち", match: ["実行待ち", "未着手"], color: "#94A3B8" },
  { label: "実行中", match: ["実行中", "オーナー問い合わせ中"], color: "#318F89" },
  { label: "オーナーレビュー中", match: ["オーナーレビュー中"], color: "#B5760F" },
  { label: "完了", match: ["完了"], color: "#1A3556" },
  { label: "ブロック", match: ["ブロック"], color: "#C0392B" },
]

/** ask_owner 待ち（status に「問い合わせ」を含む）かどうか */
function isAwaitingOwner(status: string): boolean {
  return (status || "").includes("問い合わせ")
}

const ASK_LEVELS = ["質問せず進めて（任せる）", "必要なときだけ質問", "都度確認して"]

// タスクの company / group スコープは撤去（2026-07-05・本番全件 personal を確認済み）。
// タスクは常に本人のみ＝スコープバッジ・スコープ絞り込みも不要になった。

// ─── Phase 3: 完了・ブロックの絞り込み ───
// 「完了」「ブロック」は大量に溜まる想定なので、この2列だけ既定で直近 N 件に絞り、
// 期間フィルタを効かせる。下書き/受付質疑・実行待ち・実行中の「生きている」列は常に全件。
const ARCHIVE_COLS = ["完了", "ブロック"]
const ARCHIVE_LIMIT = 10

type PeriodFilter = "all" | "week" | "month"

/** 今週（月曜 00:00・ローカル＝JST）の開始時刻。 */
function startOfWeek(now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dow = (d.getDay() + 6) % 7 // 月=0 … 日=6
  d.setDate(d.getDate() - dow)
  return d
}

/** 一覧の表示順（createdAt 降順・従来どおり）。API は updatedAt 降順のページで返す。 */
function sortByCreatedAtDesc(list: BoardTask[]): BoardTask[] {
  return [...list].sort((a, b) =>
    String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
  )
}

/** 期間絞り込み（完了・ブロック列のみに効く）。updatedAt が解釈不能なら隠さない（安全側）。 */
function withinPeriod(updatedAt: string | undefined, period: PeriodFilter): boolean {
  if (period === "all") return true
  if (!updatedAt) return true
  const t = new Date(updatedAt)
  if (isNaN(t.getTime())) return true
  const now = new Date()
  if (period === "week") return t >= startOfWeek(now)
  return t >= new Date(now.getFullYear(), now.getMonth(), 1) // month
}

/** 列1つ分の絞り込み結果（表示するタスク・「もっと見る」で隠れている件数・総件数）。 */
interface ColumnResult {
  /** この列の定義（ラベル・ステータス一致条件・色） */
  col: (typeof COLS)[number]
  /** 絞り込み・省略後に実際に描画するタスク */
  tasks: BoardTask[]
  /** 直近 N 件に省略して隠れている件数（アーカイブ列以外は常に 0） */
  hiddenCount: number
  /** 列ヘッダ・チップに出す総件数（tasks.length + hiddenCount） */
  total: number
}

/**
 * 列ごとの絞り込み計算（ステータス一致・キーワード・完了/ブロックの期間＋直近 N 件省略）を
 * 一括で行う。デスクトップ（6列横並び）とモバイル（チップ＋単一列リスト）の両方がこの
 * 結果をそのまま参照することで、件数表示や「もっと見る」の挙動が画面幅で食い違わないように
 * する（計算ロジックの単一ソース化）。
 */
function buildColumnResults(
  tasks: BoardTask[],
  keyword: string,
  period: PeriodFilter,
  expandedCols: Record<string, boolean>
): ColumnResult[] {
  const kw = keyword.trim().toLowerCase()
  return COLS.map((col) => {
    const isArchive = ARCHIVE_COLS.includes(col.label)
    // 1) 列のステータス一致
    let colTasks = tasks.filter((t) => col.match.some((m) => (t.status || "").includes(m)))
    // 2) キーワードは全列に適用（任意・既定では素通し）
    if (kw) colTasks = colTasks.filter((t) => (t.title || "").toLowerCase().includes(kw))
    // 3) 完了・ブロック列のみ: 期間フィルタ＋直近順＋直近 N 件（もっと見るで展開）
    let hiddenCount = 0
    if (isArchive) {
      if (period !== "all") colTasks = colTasks.filter((t) => withinPeriod(t.updatedAt, period))
      colTasks = [...colTasks].sort((a, b) =>
        String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))
      )
      if (!expandedCols[col.label] && colTasks.length > ARCHIVE_LIMIT) {
        hiddenCount = colTasks.length - ARCHIVE_LIMIT
        colTasks = colTasks.slice(0, ARCHIVE_LIMIT)
      }
    }
    return { col, tasks: colTasks, hiddenCount, total: colTasks.length + hiddenCount }
  })
}

/** カンバンカード1件の見た目。デスクトップの列・モバイルの単一列リストの双方から使う共通部品。 */
export function TaskCard({
  task: t,
  col,
  onSelect,
}: {
  task: BoardTask
  col: (typeof COLS)[number]
  onSelect: (task: BoardTask) => void
}) {
  return (
    <button
      onClick={() => onSelect(t)}
      // モックの .task 準拠: 白カード・左端 3px の列色バー・控えめな多層シャドウ
      // （rest: ごく薄い影／hover: 少し深い影＋1〜2px のリフト・150ms）。
      // 完了列のみ done-card 相当で少し薄く見せる（col はこの列自身のラベル）。
      className={`relative w-full text-left rounded-[10px] border border-line bg-card py-2.5 pl-3.5 pr-3 shadow-sm transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 ${
        col.label === "完了" ? "opacity-[.78]" : ""
      }`}
      style={{ borderLeftColor: col.color, borderLeftWidth: 3 }}
    >
      <div className="text-[13px] font-semibold leading-snug">{t.title}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-1 font-mono text-[10.5px] text-muted-foreground">
        <span>{t.taskId}</span>
        <span>・</span>
        <span>{(t.status || "").slice(0, 12)}</span>
        {t.dueDate && (
          <>
            <span>・</span>
            <span>期限 {t.dueDate}</span>
          </>
        )}
      </div>
      {((t.status || "").includes("下書き") ||
        isAwaitingOwner(t.status) ||
        (t.aheadCount ?? 0) > 0) && (
        <div className="mt-1.5 flex items-center gap-1">
          {(t.status || "").includes("下書き") && (
            <span className="rounded border border-line bg-paper px-1 py-0.5 text-[10px] text-muted-foreground">
              下書き
            </span>
          )}
          {isAwaitingOwner(t.status) && (
            <span className="rounded border border-line bg-gold-bg px-1 py-0.5 text-[10px] text-gold-deep">
              ❓回答待ち
            </span>
          )}
          {/* 並列化バッチ: ユーザー単位直列の待ち行列。実行待ち系で待機中のタスクのみ表示。 */}
          {(t.aheadCount ?? 0) > 0 && (
            <span className="rounded border border-line bg-paper px-1 py-0.5 text-[10px] text-muted-foreground">
              順番待ち: 前に {t.aheadCount} 件
            </span>
          )}
        </div>
      )}
    </button>
  )
}

/** テナント視点「今何本実行中か（N/10）」の小さな表示。
 *  status が null（未取得・取得失敗）なら何も描画しない＝静かに非表示。
 *  running=0 のときは目立たせない（muted）。 */
export function QueueStatusBadge({ status }: { status: QueueStatus | null }) {
  if (!status) return null
  const active = status.running > 0
  return (
    <span
      className={`rounded-full px-2 py-1 text-[11px] font-mono ${
        active ? "bg-teal-bg text-teal" : "text-muted-foreground"
      }`}
    >
      実行中 {status.running}/{status.max}
    </span>
  )
}

/**
 * 1列分のタスク一覧本体（カード列・「該当なし」・「もっと見る/閉じる」）。
 * デスクトップは6列分をこれで並べ、モバイルは選択中の1列分だけをこれで描画する
 * （どちらも同じ ColumnResult を渡すだけで挙動が揃う）。
 */
function ColumnTaskList({
  result,
  onSelect,
  expanded,
  onToggleExpand,
}: {
  result: ColumnResult
  onSelect: (task: BoardTask) => void
  expanded: boolean
  onToggleExpand: (expand: boolean) => void
}) {
  const { col, tasks: colTasks, hiddenCount } = result
  const isArchive = ARCHIVE_COLS.includes(col.label)
  return (
    <>
      <div className="space-y-2">
        {colTasks.map((t) => (
          <TaskCard key={t.taskId} task={t} col={col} onSelect={onSelect} />
        ))}
        {isArchive && colTasks.length === 0 && (
          <p className="px-1 py-3 text-[11px] text-muted-foreground">該当なし</p>
        )}
      </div>
      {/* 完了・ブロック列のみ: 直近 N 件で省略中なら展開、展開中なら畳む */}
      {isArchive && hiddenCount > 0 && (
        <button
          className="mt-2 w-full rounded-md border border-line bg-card py-1.5 text-[11px] text-muted-foreground hover:bg-accent"
          onClick={() => onToggleExpand(true)}
        >
          もっと見る（残り {hiddenCount} 件）
        </button>
      )}
      {isArchive && expanded && colTasks.length > ARCHIVE_LIMIT && (
        <button
          className="mt-2 w-full rounded-md border border-line bg-card py-1.5 text-[11px] text-muted-foreground hover:bg-accent"
          onClick={() => onToggleExpand(false)}
        >
          閉じる（直近 {ARCHIVE_LIMIT} 件に戻す）
        </button>
      )}
    </>
  )
}

export default function BoardPage() {
  const { user } = useAuth()
  const idToken = user?.id_token || ""
  const myEmail = (user?.profile?.email as string) || ""

  const [tasks, setTasks] = useState<BoardTask[]>([])
  const [selected, setSelected] = useState<BoardTask | null>(null)
  const [showRegister, setShowRegister] = useState(false)
  const [showRecurrences, setShowRecurrences] = useState(false)
  const [error, setError] = useState("")
  // ログインユーザー情報（所属グループ＝スコープ選択肢・バッジのグループ名表示に使う）
  const [me, setMe] = useState<BoardMe | null>(null)
  // 本人スコープの利用クレジット（常時表示メーター・月次バナー用）。
  const { usage } = useMyUsage()
  // テナント内の実行中並列数（ユーザー単位直列・テナント内最大10並列化）。
  // 取得失敗時は null のまま＝バッジを静かに非表示にする（画面にエラーは出さない）。
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null)

  // Phase 3: 絞り込み。keyword は全列、period は完了・ブロック列のみに効く。
  // 完了・ブロックは既定で直近 ARCHIVE_LIMIT 件のみ表示し、列ごとに「もっと見る」で展開。
  const [keyword, setKeyword] = useState("")
  const [period, setPeriod] = useState<PeriodFilter>("all")
  const [expandedCols, setExpandedCols] = useState<Record<string, boolean>>({})

  // 列ごとの絞り込み結果（デスクトップ6列・モバイルのチップ+単一列リストが共通で参照する）。
  const columnResults = useMemo(
    () => buildColumnResults(tasks, keyword, period, expandedCols),
    [tasks, keyword, period, expandedCols]
  )
  // モバイル（<768px）: 列→状態チップ＋単一列リストのレイアウトに切り替える。既定は先頭列。
  const isMobile = useIsMobile()
  const [mobileCol, setMobileCol] = useState<string>(COLS[0].label)
  const selectedColumnResult =
    columnResults.find((r) => r.col.label === mobileCol) ?? columnResults[0]

  // 初回ロード: nextCursor が尽きるまで全ページを歩いて全件取得する（従来と同じ
  // 「完全な一覧」— テナント 1MB 超で古いタスクが黙って切れるバグだけが直る）。
  // ページは updatedAt 降順なので、歩行中に更新された項目は既読側（先頭）へ移り
  // 取りこぼす可能性があるが、15 秒ポーリングの先頭ページで必ず拾われる。
  const loadAll = useCallback(async () => {
    if (!idToken) return
    try {
      const byId = new Map<string, BoardTask>()
      let cursor: string | undefined
      do {
        const page = await listTasks(idToken, cursor)
        // 万一の重複は先勝ち（先のページほど updatedAt が新しい＝フレッシュ）
        for (const t of page.tasks) if (!byId.has(t.taskId)) byId.set(t.taskId, t)
        cursor = page.nextCursor ?? undefined
      } while (cursor)
      setTasks(sortByCreatedAtDesc(Array.from(byId.values())))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [idToken])

  // テナント実行中並列数の取得。失敗しても画面には出さず、バッジを消すだけにする。
  const refreshQueueStatus = useCallback(async () => {
    if (!idToken) return
    try {
      setQueueStatus(await getQueueStatus(idToken))
    } catch {
      setQueueStatus(null)
    }
  }, [idToken])

  // 15秒ポーリング＋操作後の更新: 先頭ページ（最新200件）だけ取得し、既存 state に
  // taskId でマージする（新規は追加・既存は置換・先頭ページに居ないものは保持）。
  // ステータス変化は必ず updatedAt を進める＝先頭ページに現れるので、
  // 生きている列の正確性はこれで保たれる。queue-status も同じ周期に相乗りさせる
  // （別タイマーは作らない）。
  const refresh = useCallback(async () => {
    if (!idToken) return
    try {
      const page = await listTasks(idToken)
      setTasks((prev) => {
        const byId = new Map(prev.map((t) => [t.taskId, t] as const))
        for (const t of page.tasks) byId.set(t.taskId, t)
        return sortByCreatedAtDesc(Array.from(byId.values()))
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    refreshQueueStatus()
  }, [idToken, refreshQueueStatus])

  useEffect(() => {
    loadAll()
    refreshQueueStatus()
    const t = setInterval(refresh, 15000)
    return () => clearInterval(t)
  }, [loadAll, refresh, refreshQueueStatus])

  // 通知メールからの直リンク（/board?task=<id>）: 初回の全件ロード完了後に
  // 該当タスクを開く。一度処理したら URL からパラメータを外す（閉じた後の
  // ポーリングで再度開かないように）。
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandledRef = useRef(false)
  useEffect(() => {
    const taskId = searchParams.get("task")
    if (deepLinkHandledRef.current || !taskId || tasks.length === 0) return
    deepLinkHandledRef.current = true
    const t = tasks.find((x) => x.taskId === taskId)
    if (t) {
      setSelected(t)
    } else {
      setError("リンク先のタスクが見つかりませんでした（削除された可能性があります）")
    }
    setSearchParams({}, { replace: true })
  }, [tasks, searchParams, setSearchParams])

  useEffect(() => {
    if (!idToken) return
    getMe(idToken)
      .then(setMe)
      .catch(() => setMe(null)) // 取得失敗でもボード自体は使える（スコープ表示が簡略になるだけ）
  }, [idToken])

  return (
    <div className="flex flex-col h-dvh bg-background">
      {/* Chat/Board 共通ヘッダー（紺グラデ・セグメントタブが現在地を示すため
          個別の「← Chat」リンクと「Board」見出しは廃止）。 */}
      <AppHeader usage={usage} />
      {/* テナント月次の利用枠バナー（上限設定時のみ表示）。 */}
      <TenantMonthlyBanner usage={usage} />

      {error && (
        <div className="bg-destructive/10 text-destructive text-sm px-4 py-2">
          {error}
          <button className="ml-2 underline" onClick={() => setError("")}>
            閉じる
          </button>
        </div>
      )}

      {/* Phase 3: 絞り込みバー。キーワードは全列、期間は完了・ブロック列のみ。
          タスク登録・定期タスクの導線はヘッダから移設し、絞り込みと同じバーの右端にまとめる
          （ml-auto で右寄せ・モック準拠で定期タスクが左／タスク登録が右）。 */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-line bg-card text-xs">
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="🔍 キーワード（タイトル）"
          className="h-8 w-56 max-w-full rounded-md border border-line bg-background px-2 text-base md:text-xs"
        />
        <label className="flex items-center gap-1">
          <span className="text-muted-foreground">期間</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodFilter)}
            className="h-8 rounded-md border border-line bg-background px-2"
          >
            <option value="all">全期間</option>
            <option value="week">今週</option>
            <option value="month">今月</option>
          </select>
        </label>
        <span className="text-[11px] text-muted-foreground">
          期間は「完了・ブロック」列のみに適用（生きている列は常に全件）
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* テナント視点「今何本実行中か（N/10）」。取得失敗時は QueueStatusBadge が null を返し非表示。 */}
          <QueueStatusBadge status={queueStatus} />
          <Button size="sm" variant="outline" onClick={() => setShowRecurrences(true)}>
            🔁 定期タスク
          </Button>
          {/* モバイルは右下 FAB に同機能があるので、ツールバー側は md 以上のみ表示。 */}
          <Button size="sm" className="hidden md:inline-flex" onClick={() => setShowRegister(true)}>
            ＋ タスク登録
          </Button>
        </div>
      </div>

      {isMobile ? (
        // モバイル（<768px）: 横スクロールの状態チップ（全6列・件数付き）＋選択中の列のみを
        // 縦1列で表示。デスクトップと同じ columnResults / ColumnTaskList を使うため、
        // 件数や「もっと見る」の挙動はデスクトップと完全に一致する。
        <main className="flex-1 min-h-0 flex flex-col bg-paper">
          <div className="flex gap-2 overflow-x-auto px-4 py-3 border-b border-line bg-card">
            {columnResults.map((r) => {
              const isSelected = r.col.label === mobileCol
              return (
                <button
                  key={r.col.label}
                  onClick={() => setMobileCol(r.col.label)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs whitespace-nowrap transition-colors ${
                    isSelected
                      ? "bg-night text-white"
                      : "bg-card border border-line text-muted-foreground"
                  }`}
                >
                  {r.col.label}
                  <span className="ml-1 font-mono text-[11px]">({r.total})</span>
                </button>
              )
            })}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {selectedColumnResult && (
              <ColumnTaskList
                result={selectedColumnResult}
                onSelect={setSelected}
                expanded={!!expandedCols[selectedColumnResult.col.label]}
                onToggleExpand={(expand) =>
                  setExpandedCols((p) => ({ ...p, [selectedColumnResult.col.label]: expand }))
                }
              />
            )}
          </div>
        </main>
      ) : (
        // デスクトップ（md 以上）: 従来どおり6列横スクロール。見た目は変更なし。
        <main className="flex-1 overflow-x-auto p-4 bg-paper">
          <div className="flex gap-3 min-h-full">
            {columnResults.map((r) => (
              // 列はモック準拠でフラットに（背景の受け皿箱を廃止・幅 w-64 のままカードを直置き）。
              <div key={r.col.label} className="w-64 shrink-0">
                {/* 列ヘッダ: 色ドット（8px）＋ラベル＋件数（mono・右寄せ）の一行。 */}
                <div className="flex items-center gap-2 border-b border-line px-1 pb-2 mb-2">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: r.col.color }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate text-xs font-semibold">{r.col.label}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    ({r.total})
                  </span>
                </div>
                <ColumnTaskList
                  result={r}
                  onSelect={setSelected}
                  expanded={!!expandedCols[r.col.label]}
                  onToggleExpand={(expand) =>
                    setExpandedCols((p) => ({ ...p, [r.col.label]: expand }))
                  }
                />
              </div>
            ))}
          </div>
        </main>
      )}

      {/* モバイル専用のタスク登録 FAB（ツールバーの「＋ タスク登録」と同機能）。
          bottom は下部タブバー（56px＋ホームバー safe-area）の上に浮かせる。 */}
      {isMobile && (
        <button
          type="button"
          aria-label="タスク登録"
          onClick={() => setShowRegister(true)}
          className="fixed right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-night text-white shadow-lg"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom))" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {/* モバイル専用の下部タブバー（チャット/ボード切替）。flex 末尾に置くことで main の
          flex-1 が自動的に縮み、コンテンツがタブバーに隠れない。 */}
      <BottomTabBar />

      {selected && (
        <TaskDetail
          idToken={idToken}
          task={selected}
          onClose={() => {
            setSelected(null)
            refresh()
          }}
        />
      )}
      {showRegister && (
        <RegisterDialog
          idToken={idToken}
          myEmail={myEmail}
          onClose={(registered) => {
            setShowRegister(false)
            if (registered) refresh()
          }}
        />
      )}
      {showRecurrences && (
        <RecurrenceList
          idToken={idToken}
          meSub={me?.sub ?? ""}
          onClose={() => setShowRecurrences(false)}
        />
      )}
    </div>
  )
}

/** タスク詳細: スレッド・成果物・回答/差し戻し/承認・受付開始・添付 */
function TaskDetail({
  idToken,
  task,
  onClose,
}: {
  idToken: string
  task: BoardTask
  onClose: () => void
}) {
  const [thread, setThread] = useState<ThreadEntry[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [preview, setPreview] = useState<Artifact | null>(null)
  const [knowledgeScope, setKnowledgeScope] = useState("personal")
  // ナレッジ還流（バッチC）: 登録先スコープ候補（全体は admin のみ／個人／所属グループ）
  const [kScopeOpts, setKScopeOpts] = useState<{ scope: string; name: string }[]>([])
  const [kTeamAllowed, setKTeamAllowed] = useState(false)
  // 提案本文の確認・編集用ローカル state（提案値で初期化）
  const kp = task.knowledgeProposal
  const [kTitle, setKTitle] = useState(kp?.title ?? "")
  const [kBody, setKBody] = useState(kp?.body_md ?? "")
  const [kDesc, setKDesc] = useState(kp?.description ?? "")
  const [reply, setReply] = useState("")
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState("")
  // ブロック/再投入後にモーダル内の状態表示を即時更新するためのローカル override
  const [statusOverride, setStatusOverride] = useState<string | null>(null)
  // 完了の定義は長文（確定ブリーフ級）になり得るため既定は1行に畳む
  // （iPhone で定義文が画面を占拠し成果物に辿り着けなかった実走報告 2026-07-06）
  const [showDoneDef, setShowDoneDef] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      setThread(await getThread(idToken, task.taskId))
      setArtifacts(await getArtifacts(idToken, task.taskId))
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    }
  }, [idToken, task.taskId])

  useEffect(() => {
    load()
    const t = setInterval(load, 10000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [thread.length])

  // ナレッジ還流（バッチC）: 登録先スコープ候補を取得し、提案の推奨スコープを既定にする。
  // Settings の KnowledgeDialog と同じパターン（全体=admin のみ・個人・所属グループ）。
  useEffect(() => {
    const proposal = task.knowledgeProposal
    setKTitle(proposal?.title ?? "")
    setKBody(proposal?.body_md ?? "")
    setKDesc(proposal?.description ?? "")
    getKnowledgeScopes(idToken)
      .then((s) => {
        setKScopeOpts(s.groups || [])
        setKTeamAllowed(!!s.team)
        // 推奨スコープを既定に。team 推奨でも admin でなければ個人へフォールバック。
        const rec = proposal?.recommendedScope || "personal"
        if (rec === "team") setKnowledgeScope(s.team ? "team" : "personal")
        else setKnowledgeScope(rec)
      })
      .catch(() => {
        setKScopeOpts([])
        setKTeamAllowed(false)
        setKnowledgeScope("personal")
      })
  }, [idToken, task.taskId, task.knowledgeProposal])

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    setMsg("")
    try {
      await fn()
      setMsg(done)
      setReply("")
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const effStatus = statusOverride ?? task.status ?? ""
  const isDraft = effStatus.includes("下書き")
  const isReview = effStatus.includes("レビュー中")
  const isQuestion = effStatus.includes("問い合わせ中")
  const isBlocked = effStatus.includes("ブロック")
  const isDone = effStatus.includes("完了")
  // 記憶の手入れタスク（バッチB）は承認＝適用 / 差し戻し＝見送り。文言を変える
  const isCuration = task.taskType === "memory_curation"
  // ステータスチップの配色（表示は effStatus 全文のまま・情報は落とさない）。
  // 実行中＝青緑（オーナー問い合わせ中もここに含む）・レビュー系＝金・完了＝紺・ブロック＝赤、
  // それ以外（下書き・受付質疑中・実行待ち等）は中立色。
  const statusChipClass = isBlocked
    ? "bg-red-bg text-red"
    : isDone
      ? "bg-night text-white"
      : isReview
        ? "bg-gold-bg text-gold-deep"
        : effStatus.includes("実行中") || isQuestion
          ? "bg-teal-bg text-teal"
          : "bg-paper text-muted-foreground border border-line"
  // 成果物・ナレッジ提案の有無（右サイドパネルの表示条件に使う）。
  const hasArtifacts = artifacts.length > 0
  const showKnowledgeProposal =
    !!task.knowledgeProposal &&
    thread.some((e) => e.type === "knowledge_proposal") &&
    !thread.some((e) => e.type === "knowledge_registered") &&
    !thread.some((e) => e.type === "knowledge_dismissed")

  // オーナーレビュー中・完了では右パネルの主役＝成果物（レビュー指摘 2026-07-12）。
  // 並びを 成果物→ナレッジ提案→概要→完了の定義 に入れ替え、長文になりがちな
  // 完了の定義は md 以上でも既定で畳む（開いた瞬間に成果物が見える）。
  const reviewFirst = isReview || isDone

  // 完了の定義: 折りたたみは showDoneDef トグルで共有。
  // 通常時: md 以上は全文・md 未満は1行折りたたみ（2026-07-06 iPhone 実走対処）。
  // reviewFirst 時: 画面幅によらず既定1行（最下部に置くため場所を取らせない）。
  const doneDefSection = (
            <div className="p-3">
              <div className="mb-1 text-xs font-semibold text-primary">完了の定義</div>
              {task.doneDef ? (
                <button
                  type="button"
                  onClick={() => setShowDoneDef((v) => !v)}
                  className="block w-full text-left text-xs text-muted-foreground"
                >
                  <span
                    className={
                      showDoneDef
                        ? "whitespace-pre-wrap"
                        : reviewFirst
                          ? "line-clamp-1"
                          : "line-clamp-1 md:line-clamp-none md:whitespace-pre-wrap"
                    }
                  >
                    {task.doneDef}
                  </span>
                  <span
                    className={`text-[10px] underline text-muted-foreground/80 ${
                      reviewFirst ? "" : "md:hidden"
                    }`}
                  >
                    {showDoneDef ? "たたむ" : "全文を見る"}
                  </span>
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">（未設定）</p>
              )}
            </div>
  )

  // 概要: 期限・問い合わせ許容度・通知先。値が無いフィールドは行ごと非表示。
  const summarySection = (
            <div className="p-3">
              <div className="mb-1 text-xs font-semibold text-primary">概要</div>
              <dl className="space-y-1 text-xs">
                {task.dueDate && (
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-muted-foreground shrink-0">期限</dt>
                    <dd className="text-right">{task.dueDate}</dd>
                  </div>
                )}
                {typeof task.askLevel === "number" && ASK_LEVELS[task.askLevel] && (
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-muted-foreground shrink-0">問い合わせ許容度</dt>
                    <dd className="text-right">{ASK_LEVELS[task.askLevel]}</dd>
                  </div>
                )}
                {task.notifyTo && (
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-muted-foreground shrink-0">通知先</dt>
                    <dd className="truncate text-right font-mono">{task.notifyTo}</dd>
                  </div>
                )}
              </dl>
            </div>
  )

  // 成果物: 「見る」（プレビュー可能なもののみ）と「ダウンロード」の両導線を維持する。
  // md 未満では縦積みの先頭付近に出るため到達しやすさも維持（2026-07-06 対処の継承）。
  const artifactsSection = hasArtifacts ? (
              <div className="p-3">
                <div className="mb-1 text-xs font-semibold text-primary">
                  成果物 {artifacts.length}
                </div>
                <ul className="space-y-1">
                  {artifacts.map((a) => (
                    <li
                      key={a.name}
                      className="flex items-center overflow-hidden rounded border border-line bg-paper text-xs"
                    >
                      {a.previewKind && a.previewKind !== "none" && (
                        <button
                          className="shrink-0 border-r border-line px-2 py-1 hover:bg-accent"
                          onClick={() => setPreview(a)}
                        >
                          👁 見る
                        </button>
                      )}
                      <a
                        href={a.url}
                        className="min-w-0 flex-1 truncate px-2 py-1 hover:bg-accent"
                        download
                      >
                        📄 {a.name} ({formatSizeKB(a.size)})
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
  ) : null

  // ナレッジ還流の提案（バッチC）: 本文を確認・編集し、スコープを選んで登録できる。
  // 提案は worker が成果物完成後に作る。提案の本文は task.knowledgeProposal にある。
  const knowledgeSection = showKnowledgeProposal ? (
              <div className="space-y-2 p-3 text-sm">
                <div className="font-semibold">📚 ナレッジ登録の提案があります</div>
                <p className="text-xs text-muted-foreground">
                  このやりとりに再利用できそうな知識が見つかりました。内容を確認・編集し、登録先を選んで登録できます。
                </p>
                <label className="block text-xs font-medium">タイトル（ファイル名）</label>
                <Input
                  className="h-8 text-xs"
                  value={kTitle}
                  onChange={(ev) => setKTitle(ev.target.value)}
                  placeholder="ナレッジの見出し"
                />
                <label className="block text-xs font-medium">一行説明（目次に出る・任意）</label>
                <Input
                  className="h-8 text-xs"
                  value={kDesc}
                  onChange={(ev) => setKDesc(ev.target.value)}
                  placeholder="何の知識か（空なら自動要約）"
                />
                <label className="block text-xs font-medium">本文（編集できます）</label>
                <Textarea
                  value={kBody}
                  onChange={(ev) => setKBody(ev.target.value)}
                  rows={8}
                  className="text-xs font-mono"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="h-8 rounded-md border bg-transparent px-2 text-xs"
                    value={knowledgeScope}
                    onChange={(ev) => setKnowledgeScope(ev.target.value)}
                  >
                    {/* 全体（会社ナレッジ）は admin 以上のみ */}
                    {kTeamAllowed && <option value="team">全体（テナント共有）</option>}
                    <option value="personal">個人</option>
                    {kScopeOpts.map((g) => (
                      <option key={g.scope} value={g.scope}>
                        グループ: {g.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    disabled={busy || !kBody.trim()}
                    onClick={() =>
                      act(
                        () =>
                          applyKnowledge(idToken, task.taskId, {
                            scope: knowledgeScope,
                            content: kBody,
                            name: kTitle,
                            description: kDesc,
                          }),
                        "ナレッジに登録しました"
                      )
                    }
                  >
                    ナレッジに登録
                  </Button>
                  {/* 見送り（登録しない）: apply のミラー。S3 保存なしで提案を消費し、
                      スレッドに knowledge_dismissed を残してボックスを即時に消す。 */}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => dismissKnowledge(idToken, task.taskId),
                        "ナレッジ登録を見送りました"
                      )
                    }
                  >
                    登録しない
                  </Button>
                </div>
              </div>
  ) : null

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* モックの .modal 準拠の2ペイン（md 以上）。md 未満は本体を flex-col に戻し、
          「現行同様の縦積み」へフォールバックする（スレッド・成果物・完了の定義・
          ナレッジ提案の並び順が変わるだけで要素・機能は増減しない）。
          既定の sm:max-w-lg(512px) を sm: 込みで上書きしないと desktop で効かないため
          sm:max-w も指定。w-[92vw] で画面からのはみ出しを防ぎ小画面でも破綻させない。 */}
      <DialogContent className="w-[92vw] max-w-5xl sm:max-w-5xl max-h-[85dvh] flex flex-col shadow-2xl">
        <DialogHeader>
          <DialogTitle className="pr-8 text-primary">{task.title}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{task.taskId}</span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px] ${statusChipClass}`}
            >
              {effStatus}
            </span>
          </div>
        </DialogHeader>

        {isBlocked && (
          <div className="rounded border bg-red-50 p-3 text-sm flex items-center justify-between">
            <span>
              このタスクは<b>ブロック（中止）</b>中です。worker は実行しません。
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await unblockTask(idToken, task.taskId)
                  setStatusOverride("実行待ち")
                }, "ブロックを解除して再投入しました")
              }
            >
              再投入（ブロック解除）
            </Button>
          </div>
        )}

        {isDraft && (
          <div className="rounded border bg-orange-50 p-3 text-sm flex items-center justify-between">
            <span>
              このタスクは<b>下書き</b>です。AI はまだ何も読んでいません。
            </span>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => act(() => startIntake(idToken, task.taskId), "受付を開始しました")}
            >
              受付質疑を開始
            </Button>
          </div>
        )}

        {/* 本体: md 以上はスレッド（左）＋概要サイドパネル（右）の2ペイン、
            md 未満は order で並び替えて縦積み（サイドパネルの内容が先・スレッドが後）にする。
            md 未満は外側（このラッパー）だけがスクロールし現行と同じ1本のスクロール領域になり、
            md 以上は左右それぞれが独立スクロールする（下の操作バーは常に見える）。 */}
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          {/* 右ペイン（md 未満では先頭に出る）。並び順はステータスで切替:
              reviewFirst（オーナーレビュー中・完了）はレビューの主役=成果物を最上部に、
              それ以外は現行どおり完了の定義から。各セクションの実体は上の const 群。 */}
          <aside className="order-1 divide-y divide-line border-b border-line bg-card md:order-2 md:min-h-0 md:w-[290px] md:shrink-0 md:overflow-y-auto md:border-b-0 md:border-l">
            {reviewFirst ? (
              <>
                {artifactsSection}
                {knowledgeSection}
                {summarySection}
                {doneDefSection}
              </>
            ) : (
              <>
                {doneDefSection}
                {summarySection}
                {artifactsSection}
                {knowledgeSection}
              </>
            )}
          </aside>

          {/* 左ペイン（md 未満では末尾に出る）: スレッド。吹き出しの意味構造は現行のまま
              （owner=右寄せ・紺／agent=左寄せ・白カード／system=中央・小さく淡く）。 */}
          <div className="order-2 min-h-[200px] space-y-2 bg-paper p-3 md:order-1 md:min-h-0 md:flex-1 md:overflow-y-auto">
            {thread.map((e) => (
              <div
                key={e.seq}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  e.author === "owner"
                    ? "ml-auto bg-primary text-primary-foreground"
                    : e.author === "system"
                      ? "mx-auto bg-muted text-muted-foreground text-xs py-1"
                      : "bg-card border border-line"
                }`}
              >
                {e.author !== "owner" && e.author !== "system" && (
                  <div className="text-[10px] text-muted-foreground mb-0.5">
                    agent / {e.type}
                  </div>
                )}
                {e.text}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        </div>
        {preview && <PreviewModal item={preview} onClose={() => setPreview(null)} />}

        <div className="border-t border-line pt-2 space-y-2 shrink-0">
          {msg && <div className="text-xs text-muted-foreground">{msg}</div>}
          {!isBlocked && (
            <Textarea
              placeholder={
                isQuestion
                  ? "エージェントの質問に回答..."
                  : isReview
                    ? "差し戻し理由（修正してほしい点）..."
                    : "回答・追記..."
              }
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
            />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {!isBlocked && !isCuration && (
            <Button
              size="sm"
              disabled={busy || !reply.trim()}
              onClick={() => act(() => answerTask(idToken, task.taskId, reply), "回答しました（再開キューへ）")}
            >
              回答して再開
            </Button>
            )}
            {isReview && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || (!isCuration && !reply.trim())}
                  onClick={() =>
                    act(() => sendbackTask(idToken, task.taskId, reply || (isCuration ? "見送り" : "")),
                      isCuration ? "見送りました（記憶は変更していません）" : "差し戻しました")
                  }
                >
                  {isCuration ? "見送る" : "差し戻し"}
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true)
                    setMsg("")
                    try {
                      const r = await approveTask(idToken, task.taskId)
                      setMsg(isCuration ? "承認しました（記憶に適用）" : "承認しました（完了）")
                      // 完了の先の提案（バッチF）: あれば一言確認して、置くなら登録する
                      if (r.nextStep?.shouldSuggest) {
                        const ns = r.nextStep
                        if (
                          window.confirm(
                            `この続きとして「${ns.title}」を置きますか？\n（${ns.reason}）`
                          )
                        ) {
                          await registerTask(idToken, {
                            title: ns.title,
                            done: ns.done_definition,
                          })
                          setMsg("次の一手を Board に置きました")
                        }
                      }
                      // 承認が完全に成功した直後にモーダルを閉じる。
                      // load()（詳細内の再取得）は内部 catch で throw しないため、
                      // close 前に挟むと再取得失敗時にも閉じてしまう。閉じるなら
                      // 詳細の再取得は不要で、親 Board 一覧の更新は onClose
                      // （setSelected(null); refresh()）が担う。
                      onClose()
                    } catch (e) {
                      setMsg(e instanceof Error ? e.message : String(e))
                    } finally {
                      setBusy(false)
                    }
                  }}
                >
                  {isCuration ? "承認して適用" : "承認（完了）"}
                </Button>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f)
                  act(() => uploadAttachment(idToken, task.taskId, f), `添付しました: ${f.name}`)
                e.target.value = ""
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              📎 添付
            </Button>
            {/* 中止（ブロック）: 進行中・問い合わせ中・実行待ちなどを手動で止める。完了/ブロック中は出さない */}
            {!isBlocked && !isDone && !isDraft && (
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                disabled={busy}
                onClick={() => {
                  const reason = window.prompt("中止（ブロック）の理由（任意）", "")
                  if (reason === null) return // キャンセル
                  act(async () => {
                    await blockTask(idToken, task.taskId, reason)
                    setStatusOverride("ブロック")
                  }, "タスクをブロック（中止）しました")
                }}
              >
                ⛔ 中止（ブロック）
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** 登録フォーム: 下書きはタイトルのみ必須／受付開始時に完了の定義・通知先を必須化。
 *  参考ファイル添付（複数・15MB）も再現（旧 Flask UI パリティ）。 */
function RegisterDialog({
  idToken,
  myEmail,
  onClose,
}: {
  idToken: string
  myEmail: string
  onClose: (registered: boolean) => void
}) {
  const [title, setTitle] = useState("")
  const [done, setDone] = useState("")
  const [notifyTo, setNotifyTo] = useState(myEmail)
  const [notes, setNotes] = useState("")
  const [constraints, setConstraints] = useState("")
  const [due, setDue] = useState("")
  const [askLevel, setAskLevel] = useState(1)
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState("")
  const attachRef = useRef<HTMLInputElement>(null)
  // 繰り返し（定期タスク・バッチE）
  const [repeat, setRepeat] = useState(false)
  const [freq, setFreq] = useState("weekly")
  const [dayOfWeek, setDayOfWeek] = useState(0)
  const [dayOfMonth, setDayOfMonth] = useState(1)
  const [hour, setHour] = useState(9)
  const [minute, setMinute] = useState(0)

  const addFiles = (list: FileList | null) => {
    if (!list) return
    const picked = Array.from(list)
    const tooBig = picked.find((f) => f.size > 15 * 1024 * 1024)
    if (tooBig) {
      setMsg(`「${tooBig.name}」は 15MB を超えています`)
      return
    }
    setFiles((prev) => [...prev, ...picked])
  }

  const submit = async (start: boolean) => {
    // D: 下書きはタイトルのみ必須。受付開始は完了の定義＋通知先も必須。
    if (!title.trim()) {
      setMsg("タイトルは必須です")
      return
    }
    // 繰り返しが ON のときは定期タスク（テンプレート）として登録する（バッチE）
    if (repeat) {
      if (!done.trim()) {
        setMsg("定期タスクには完了の定義が必要です")
        return
      }
      setBusy(true)
      setMsg("")
      try {
        await createRecurrence(idToken, {
          title, done, notes,
          notifyTo: notifyTo || undefined,
          freq, hour, minute,
          dayOfWeek: freq === "weekly" ? dayOfWeek : undefined,
          dayOfMonth: freq === "monthly" ? dayOfMonth : undefined,
        })
        onClose(true)
      } catch (e) {
        setMsg(e instanceof Error ? e.message : String(e))
        setBusy(false)
      }
      return
    }
    if (start && (!done.trim() || !notifyTo.trim())) {
      setMsg("受付開始には完了の定義と通知先が必要です")
      return
    }
    setBusy(true)
    setMsg("")
    try {
      const { taskId } = await registerTask(idToken, {
        title,
        done: done || undefined,
        notifyTo: notifyTo || undefined,
        notes,
        constraints,
        due,
        askLevel,
      })
      // C: 参考ファイルをタスクのワークスペースへアップロード（worker が attachments/ で参照）
      for (const f of files) {
        await uploadAttachment(idToken, taskId, f)
      }
      if (start) await startIntake(idToken, taskId, { done, notifyTo })
      onClose(true)
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      {/* タスク登録モーダルも横幅拡大し詳細モーダルとトーンを揃える（sm: 込みで上書き）。
          フォームなので詳細(6xl)より控えめな 3xl に留め、w-[92vw] で小画面に追従。 */}
      <DialogContent className="w-[92vw] max-w-3xl sm:max-w-3xl max-h-[85dvh] overflow-y-auto shadow-2xl">
        <DialogHeader>
          <DialogTitle>タスク登録</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">
              タイトル <span className="text-destructive">必須</span>
            </label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold">
              完了の定義 <span className="text-muted-foreground">（受付開始時に必須）</span>
            </label>
            <Input
              placeholder="何ができたら完了か・成果物の形式"
              value={done}
              onChange={(e) => setDone(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold">
              通知先メール <span className="text-muted-foreground">（受付開始時に必須）</span>
            </label>
            <Input value={notifyTo} onChange={(e) => setNotifyTo(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold">背景・文脈</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold">制約・してはいけないこと</label>
            <Textarea
              rows={2}
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold">期限</label>
              <Input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold">問い合わせ許容度</label>
              <select
                className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                value={askLevel}
                onChange={(e) => setAskLevel(Number(e.target.value))}
              >
                {ASK_LEVELS.map((l, i) => (
                  <option key={i} value={i}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* 繰り返し（定期タスク・バッチE） */}
          <div className="rounded border p-2">
            <label className="flex items-center gap-2 text-xs font-semibold">
              <input
                type="checkbox"
                checked={repeat}
                onChange={(e) => setRepeat(e.target.checked)}
              />
              繰り返し（定期タスクにする）
            </label>
            {repeat && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <select
                  className="h-8 rounded-md border bg-transparent px-2 text-xs"
                  value={freq}
                  onChange={(e) => setFreq(e.target.value)}
                >
                  <option value="weekday">平日毎日（月〜金）</option>
                  <option value="weekly">毎週</option>
                  <option value="monthly">毎月</option>
                </select>
                {/* 平日毎日は曜日/日付の指定不要（時刻のみ） */}
                {freq === "weekly" ? (
                  <select
                    className="h-8 rounded-md border bg-transparent px-2 text-xs"
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(Number(e.target.value))}
                  >
                    {["月", "火", "水", "木", "金", "土", "日"].map((d, i) => (
                      <option key={i} value={i}>
                        {d}曜
                      </option>
                    ))}
                  </select>
                ) : freq === "monthly" ? (
                  <select
                    className="h-8 rounded-md border bg-transparent px-2 text-xs"
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>
                        {d}日
                      </option>
                    ))}
                  </select>
                ) : null}
                <input
                  type="number"
                  min={0}
                  max={23}
                  className="h-8 w-14 rounded-md border bg-transparent px-2 text-xs"
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                />
                <span className="text-xs">時</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  className="h-8 w-14 rounded-md border bg-transparent px-2 text-xs"
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                />
                <span className="text-xs">分（JST）</span>
              </div>
            )}
            {repeat && (
              <p className="text-[11px] text-muted-foreground mt-1">
                定期タスクは完了の定義が必須です。期限・添付・受付開始は使いません。
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold">参考ファイル（任意・複数可・各15MB）</label>
            <input
              ref={attachRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files)
                e.target.value = ""
              }}
            />
            <div className="flex items-center gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => attachRef.current?.click()}
              >
                📎 ファイルを追加
              </Button>
              <span className="text-xs text-muted-foreground">
                {files.length > 0 ? `${files.length} 件選択中` : "未選択"}
              </span>
            </div>
            {files.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate">
                      {f.name}（{formatSizeKB(f.size)}）
                    </span>
                    <button
                      className="text-destructive"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    >
                      取消
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {msg && <div className="text-xs text-destructive">{msg}</div>}
          <div className="flex justify-end gap-2 pt-1">
            {repeat ? (
              <Button disabled={busy} onClick={() => submit(true)}>
                定期タスクを登録
              </Button>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => submit(false)}>
                  下書きとして保存
                </Button>
                <Button disabled={busy} onClick={() => submit(true)}>
                  登録して受付開始
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}


/** 定期タスク一覧（バッチE）: 一時停止/再開/削除。自分に見えるテンプレートのみ。 */
function RecurrenceList({
  idToken,
  meSub,
  onClose,
}: {
  idToken: string
  meSub: string
  onClose: () => void
}) {
  const [items, setItems] = useState<Recurrence[]>([])
  const [msg, setMsg] = useState("")
  const [busy, setBusy] = useState(false)
  // 編集対象の定期タスク（null のときはダイアログ非表示）。
  const [editing, setEditing] = useState<Recurrence | null>(null)

  const load = useCallback(async () => {
    try {
      setItems(await listRecurrences(idToken))
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    }
  }, [idToken])

  useEffect(() => {
    load()
  }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      await load()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* 既定の sm:max-w-lg(512px) に負けないよう sm: 込みで幅を上書き（TaskDetail と同じ罠）。
          行は2段構成で折り返すため、横スクロールはどの幅でも出さない（レビュー指摘 2026-07-12）。 */}
      <DialogContent className="max-w-2xl sm:max-w-2xl max-h-[80dvh] overflow-y-auto overflow-x-hidden shadow-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🔁 定期タスク
            <span className="font-mono text-xs font-normal text-muted-foreground">
              {items.length}
            </span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            設定した周期で自動的にタスクが作られます。一時停止すると生成を止められます。
          </p>
        </DialogHeader>
        {msg && <div className="text-xs text-destructive">{msg}</div>}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            定期タスクはまだありません。タスク登録の「繰り返し」から作れます。
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((r) => (
              <div
                key={r.recurrenceId}
                className={`rounded-[10px] border border-line bg-card px-4 py-3 transition-colors hover:border-slate ${
                  r.paused ? "opacity-75" : ""
                }`}
              >
                {/* 1段目: 状態（有効=teal・一時停止=slate）＋タスク名。
                    名前は truncate せず折り返す（横スクロール撲滅・レビュー指摘 2026-07-12） */}
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-2 h-2 w-2 shrink-0 rounded-full ${r.paused ? "bg-slate" : "bg-teal"}`}
                  />
                  <span
                    className={`shrink-0 text-xs font-medium leading-6 ${r.paused ? "text-slate" : "text-teal"}`}
                  >
                    {r.paused ? "一時停止" : "有効"}
                  </span>
                  <div className="min-w-0 flex-1 break-words text-[13.5px] font-semibold leading-6">
                    {r.title}
                  </div>
                </div>
                {/* 2段目: 周期・次回・前回＋操作。flex-wrap で折り返し、はみ出させない。
                    編集はオーナー本人のみ（他人所有を編集すると 404）。一時停止/再開・削除は全員可。 */}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-4 text-xs">
                  <span className="font-mono">{r.scheduleText}</span>
                  <span className="font-mono text-muted-foreground">
                    次回 {r.paused ? "—" : r.nextRunText || "—"}
                  </span>
                  {r.lastRunDate && (
                    <span className="text-muted-foreground">前回 {r.lastRunDate}</span>
                  )}
                  <span className="ml-auto flex shrink-0 items-center gap-2">
                    {r.ownerSub === meSub && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setEditing(r)}
                      >
                        編集
                      </Button>
                    )}
                    {r.paused ? (
                      <button
                        className="text-xs text-teal underline disabled:opacity-50"
                        disabled={busy}
                        onClick={() => act(() => resumeRecurrence(idToken, r.recurrenceId))}
                      >
                        再開
                      </button>
                    ) : (
                      <button
                        className="text-xs text-muted-foreground underline disabled:opacity-50"
                        disabled={busy}
                        onClick={() => act(() => pauseRecurrence(idToken, r.recurrenceId))}
                      >
                        一時停止
                      </button>
                    )}
                    <button
                      className="text-destructive text-xs underline"
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(`「${r.title}」の定期設定を削除しますか？`)) return
                        act(() => deleteRecurrence(idToken, r.recurrenceId))
                      }}
                    >
                      削除
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      {editing && (
        <RecurrenceEditDialog
          idToken={idToken}
          recurrence={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </Dialog>
  )
}

function RecurrenceEditDialog({
  idToken,
  recurrence,
  onClose,
  onSaved,
}: {
  idToken: string
  recurrence: Recurrence
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  // 作成UI（RegisterDialog の repeat セクション）と同じ項目セットを初期値で埋める。
  const [title, setTitle] = useState(recurrence.title)
  const [done, setDone] = useState(recurrence.doneDef ?? "")
  const [notes, setNotes] = useState(recurrence.notes ?? "")
  const [notifyTo, setNotifyTo] = useState(recurrence.notifyTo ?? "")
  const [freq, setFreq] = useState(recurrence.spec?.freq ?? "weekly")
  const [dayOfWeek, setDayOfWeek] = useState(recurrence.spec?.dayOfWeek ?? 0)
  const [dayOfMonth, setDayOfMonth] = useState(recurrence.spec?.dayOfMonth ?? 1)
  const [hour, setHour] = useState(recurrence.spec?.hour ?? 9)
  const [minute, setMinute] = useState(recurrence.spec?.minute ?? 0)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState("")

  const submit = async () => {
    if (!title.trim()) {
      setMsg("タイトルは必須です")
      return
    }
    if (!done.trim()) {
      setMsg("定期タスクは完了の定義が必須です")
      return
    }
    setBusy(true)
    setMsg("")
    try {
      // 周期は全体差し替えのため freq/hour/minute と必要な dayOfWeek/dayOfMonth を全送。
      await updateRecurrence(idToken, recurrence.recurrenceId, {
        title,
        done,
        notes,
        // 編集では空文字も明示送信する（undefined だと JSON.stringify が落とし、
        // 既存の通知先を「クリアして無効化」できなくなるため）。
        notifyTo,
        freq,
        hour,
        minute,
        dayOfWeek: freq === "weekly" ? dayOfWeek : undefined,
        dayOfMonth: freq === "monthly" ? dayOfMonth : undefined,
      })
      await onSaved()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80dvh] overflow-y-auto shadow-2xl">
        <DialogHeader>
          <DialogTitle>🔁 定期タスクを編集</DialogTitle>
        </DialogHeader>
        {msg && <div className="text-xs text-destructive">{msg}</div>}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">タイトル</label>
            <input
              className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold">完了の定義</label>
            <textarea
              className="w-full rounded-md border bg-transparent px-2 py-1 text-sm"
              rows={2}
              value={done}
              onChange={(e) => setDone(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold">メモ（任意）</label>
            <textarea
              className="w-full rounded-md border bg-transparent px-2 py-1 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold">通知先（任意）</label>
            <input
              className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
              value={notifyTo}
              onChange={(e) => setNotifyTo(e.target.value)}
            />
          </div>
          {/* 周期（作成UIと同じ入力）。spec は全体差し替えのため常に全フィールド送信。 */}
          <div className="rounded border p-2">
            <label className="text-xs font-semibold">周期</label>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <select
                className="h-8 rounded-md border bg-transparent px-2 text-xs"
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
              >
                <option value="weekday">平日毎日（月〜金）</option>
                <option value="weekly">毎週</option>
                <option value="monthly">毎月</option>
              </select>
              {freq === "weekly" ? (
                <select
                  className="h-8 rounded-md border bg-transparent px-2 text-xs"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value))}
                >
                  {["月", "火", "水", "木", "金", "土", "日"].map((d, i) => (
                    <option key={i} value={i}>
                      {d}曜
                    </option>
                  ))}
                </select>
              ) : freq === "monthly" ? (
                <select
                  className="h-8 rounded-md border bg-transparent px-2 text-xs"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}日
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                type="number"
                min={0}
                max={23}
                className="h-8 w-14 rounded-md border bg-transparent px-2 text-xs"
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
              />
              <span className="text-xs">時</span>
              <input
                type="number"
                min={0}
                max={59}
                className="h-8 w-14 rounded-md border bg-transparent px-2 text-xs"
                value={minute}
                onChange={(e) => setMinute(Number(e.target.value))}
              />
              <span className="text-xs">分（JST）</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "保存中…" : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
